import { createStore } from 'zustand/vanilla';
import type { StoreApi } from 'zustand/vanilla';
import type { UnitsPreference } from '../lib/units';

export type Language = 'en' | 'de';
export type ThemePreference = 'system' | 'light' | 'dark';

export interface Settings {
  language: Language;
  units: UnitsPreference;
  theme: ThemePreference;
}

export interface SettingsActions {
  setLanguage(language: Language): void;
  setUnits(units: UnitsPreference): void;
  setTheme(theme: ThemePreference): void;
}

export type SettingsStore = StoreApi<Settings & SettingsActions>;

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const SETTINGS_KEY = 'stageplottter.settings';

export function detectLanguage(langs: readonly string[] | undefined): Language {
  for (const l of langs ?? []) {
    const p = l.toLowerCase().slice(0, 2);
    if (p === 'de' || p === 'en') return p;
  }
  return 'en';
}

export function parseSettings(raw: string | null, fallbackLanguage: Language): Settings {
  const defaults: Settings = { language: fallbackLanguage, units: 'auto', theme: 'system' };
  if (raw === null) return defaults;
  try {
    const v = JSON.parse(raw) as Partial<Record<keyof Settings, unknown>>;
    return {
      language: v.language === 'de' || v.language === 'en' ? v.language : defaults.language,
      units:
        v.units === 'metric' || v.units === 'imperial' || v.units === 'auto' ? v.units : 'auto',
      theme: v.theme === 'light' || v.theme === 'dark' || v.theme === 'system' ? v.theme : 'system',
    };
  } catch {
    return defaults;
  }
}

export function createSettingsStore(
  storage: KeyValueStorage | null,
  fallbackLanguage: Language,
): SettingsStore {
  let initial: Settings;
  try {
    initial = parseSettings(storage?.getItem(SETTINGS_KEY) ?? null, fallbackLanguage);
  } catch {
    initial = parseSettings(null, fallbackLanguage);
  }
  const store = createStore<Settings & SettingsActions>()((set) => ({
    ...initial,
    setLanguage: (language) => set({ language }),
    setUnits: (units) => set({ units }),
    setTheme: (theme) => set({ theme }),
  }));
  store.subscribe((s) => {
    try {
      storage?.setItem(
        SETTINGS_KEY,
        JSON.stringify({ language: s.language, units: s.units, theme: s.theme }),
      );
    } catch {
      // storage may be unavailable (private mode) – settings stay in memory
    }
  });
  return store;
}

function browserStorage(): KeyValueStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** App-wide settings store. */
export const settingsStore: SettingsStore = createSettingsStore(
  browserStorage(),
  detectLanguage(typeof navigator === 'undefined' ? undefined : navigator.languages),
);
